import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Trash2, Check, ChevronDown } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/AdminAuth";
import { toast } from "sonner";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/messages?per_page=100`);
      setItems(res.data.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRead = async (m) => {
    await adminApi.patch(`/admin/messages/${m.id}`, { read: !m.read });
    setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: !m.read } : x)));
  };

  const remove = async (m) => {
    if (!confirm(`Delete message from ${m.email}?`)) return;
    await adminApi.delete(`/admin/messages/${m.id}`);
    setItems((prev) => prev.filter((x) => x.id !== m.id));
    toast.success("Message deleted.");
  };

  return (
    <AdminLayout title="Messages">
      <div className="rounded-2xl bg-white border border-silver/40 shadow-card overflow-hidden" data-testid="admin-messages">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-muted text-sm">No messages yet.</div>
        ) : (
          <ul className="divide-y divide-silver/30">
            {items.map((m) => (
              <li key={m.id} className={`p-4 sm:p-5 ${m.read ? "" : "bg-ice-light/60"}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2 items-center text-sm">
                      <span className={`font-semibold ${m.read ? "text-ink" : "text-navy"}`}>{m.name}</span>
                      <span className="text-muted">&lt;{m.email}&gt;</span>
                      {!m.read && (
                        <span className="text-[10px] uppercase tracking-widest text-cyan bg-cyan/10 rounded-full px-2 py-0.5">
                          New
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm font-medium text-navy">
                      {m.subject || "(no subject)"}
                    </div>
                    <div
                      className={`mt-1.5 text-sm text-ink/70 leading-relaxed ${expanded === m.id ? "" : "line-clamp-2"}`}
                      data-testid={`admin-message-body-${m.id}`}
                    >
                      {m.message}
                    </div>
                    <div className="mt-2 text-[11px] text-muted">{(m.created_at || "").slice(0, 19).replace("T", " ")} UTC</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-ice text-muted"
                      aria-label="Toggle expand"
                    >
                      <ChevronDown size={14} className={expanded === m.id ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "")}`}
                    className="inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-full border border-navy text-navy hover:bg-ice"
                    data-testid={`admin-message-reply-${m.id}`}
                  >
                    <Mail size={12} /> Reply via email
                  </a>
                  <button
                    type="button"
                    onClick={() => toggleRead(m)}
                    className="inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-full border border-silver/60 text-ink hover:bg-ice"
                    data-testid={`admin-message-toggle-${m.id}`}
                  >
                    <Check size={12} /> Mark {m.read ? "unread" : "read"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(m)}
                    className="inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-full border border-destructive/30 text-destructive hover:bg-destructive/5"
                    data-testid={`admin-message-delete-${m.id}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
